import { Link, useParams } from 'react-router';
import { FragmentView } from '../dvi/FragmentView';
import { MathText } from '../dvi/MathText';
import { findSolutionProblem, problemUrl } from '../data';
import type { SolutionProblem } from '../types/articles';
import { formatWareki } from '../lib/wareki';
import { NotFound } from './NotFound';

/**
 * 解答集の問題ページ (/solutions/:work/(<巻>/)<書籍構造準拠のパス>)。
 * 命題・証明の環境単位 DVI 断片 (meta.toml の fragments 順) を縦に並べ、
 * 下部に前後の問題と同じ巻の問題一覧を置く (他ページと同じ導線)。
 */
export function SolutionsProblem() {
  const params = useParams<{ work: string; '*': string }>();
  const found = findSolutionProblem(params.work ?? '', params['*'] ?? '');
  if (!found) {
    return <NotFound />;
  }
  const { work, book, problems, problem } = found;
  const base = problemUrl(work.key, book?.key ?? null, problem.path);
  const index = problems.findIndex((p) => p.path === problem.path);
  const prev = index > 0 ? problems[index - 1] : null;
  const next = index < problems.length - 1 ? problems[index + 1] : null;
  const problemLink = (p: SolutionProblem, prefix?: string, suffix?: string) => (
    <Link to={problemUrl(work.key, book?.key ?? null, p.path)}>
      {prefix}
      <MathText
        text={p.title}
        formulasUrl={`${problemUrl(work.key, book?.key ?? null, p.path)}/meta.formulas.dvi`}
        pages={p.metaPages}
      />
      の解答{suffix}
    </Link>
  );
  return (
    <>
      <div className="hero">
        <p className="article-breadcrumb">
          <Link to="/math">数学</Link> / <Link to="/math/solutions">解答集</Link> /{' '}
          <Link to={`/solutions/${work.key}`}>{work.title}</Link>
        </p>
        <h1>
          {book ? `${book.title} ` : ''}
          <MathText
            text={problem.title}
            formulasUrl={`${base}/meta.formulas.dvi`}
            pages={problem.metaPages}
          />
          の解答
        </h1>
        {problem.published && (
          <p className="article-meta">
            公開日: {formatWareki(problem.published)}
            {problem.updated && problem.updated !== problem.published && (
              <> ・ 更新日: {formatWareki(problem.updated)}</>
            )}
          </p>
        )}
      </div>
      <section className="card">
        {problem.fragments.map((stem) => (
          <div className="bourbaki-fragment" key={stem}>
            <FragmentView src={`${base}/${stem}`} />
          </div>
        ))}
      </section>
      <nav className="card" aria-label="解答集の他の問題">
        <div className="problem-pager">
          <span>{prev && problemLink(prev, '‹ 前: ')}</span>
          <span>{next && problemLink(next, '次: ', ' ›')}</span>
        </div>
        <h2>
          <Link to={`/solutions/${work.key}`}>{work.title}</Link>の他の問題
        </h2>
        {book && <h3>{book.title}</h3>}
        <ul className="content-links">
          {problems.map((p) => (
            <li key={p.path}>
              {p.path === problem.path ? (
                <span className="current" aria-current="page">
                  <MathText
                    text={p.title}
                    formulasUrl={`${problemUrl(work.key, book?.key ?? null, p.path)}/meta.formulas.dvi`}
                    pages={p.metaPages}
                  />
                  の解答
                </span>
              ) : (
                problemLink(p)
              )}
            </li>
          ))}
        </ul>
      </nav>
    </>
  );
}
